const PALETTE = [
  "#2563eb",
  "#0f172a",
  "#14b8a6",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#06b6d4",
  "#84cc16",
];

const getThemeColor = (token, fallback) => {
  if (typeof window === "undefined" || !window.document?.documentElement) {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();
  return value || fallback;
};

const getChartTheme = () => ({
  tick: getThemeColor("--tm", "#9ca3af"),
  text: getThemeColor("--ts2", "#d1d5db"),
  primaryText: getThemeColor("--tp", "#f3f4f6"),
  grid: getThemeColor("--bd", "rgba(255, 255, 255, 0.12)"),
  border: getThemeColor("--bdh", "rgba(255, 255, 255, 0.2)"),
});

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

const safeText = (value) => String(value || "").trim();

const CURRENCY_CODE_REGEX =
  /\b(USD|AUD|CAD|EUR|GBP|JPY|SGD|HKD|NZD|CHF|SEK|NOK|DKK|AED|SAR|QAR|BHD|KWD|OMR|INR|PHP|THB|MYR|IDR|VND|CNY|TWD|KRW|ZAR|BRL|MXN)\b/i;

const SYMBOL_TO_CURRENCY = {
  "$": "USD",
  "£": "GBP",
  "€": "EUR",
  "¥": "JPY",
  "₱": "PHP",
  "₩": "KRW",
  "₹": "INR",
  "฿": "THB",
  "₫": "VND",
  "R$": "BRL",
};

const normalizeKey = (value) =>
  safeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9%]+/g, " ")
    .trim();

const isPercentLike = (value) => /%/.test(String(value || ""));

const getCurrencyFormatter = (currency) => {
  if (!currency) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    });
  } catch {
    return null;
  }
};

const detectCurrencyFromText = (value) => {
  const text = safeText(value);
  if (!text) return null;

  const codeMatch = text.match(CURRENCY_CODE_REGEX);
  if (codeMatch?.[1]) return codeMatch[1].toUpperCase();

  const symbolEntry = Object.entries(SYMBOL_TO_CURRENCY).find(([symbol]) =>
    text.includes(symbol)
  );
  return symbolEntry?.[1] || null;
};

const inferCurrency = (table, rows = []) => {
  const samples = [];

  if (Array.isArray(table?.headers)) samples.push(...table.headers);
  if (Array.isArray(table?.rows)) {
    table.rows.forEach((row) => {
      if (Array.isArray(row)) samples.push(...row);
    });
  }
  if (Array.isArray(rows)) {
    rows.forEach((row) => {
      if (!row) return;
      samples.push(row.raw, row.label);
    });
  }

  for (const sample of samples) {
    const currency = detectCurrencyFromText(sample);
    if (currency) return currency;
  }

  return null;
};

const formatCurrencyValue = (value, currency) => {
  const formatter = getCurrencyFormatter(currency);
  if (formatter) return formatter.format(value);
  return numberFormatter.format(value);
};

const formatCompactCurrency = (value, currency) => {
  if (!currency) return compactFormatter.format(value);

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return compactFormatter.format(value);
  }
};

const parseNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = safeText(value);
  if (!text) return null;

  const cleaned = text
    .replace(/\((.*?)\)/g, "-$1")
    .replace(/[^0-9.\-]/g, "");

  if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") {
    return null;
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const findHeaderIndex = (headers = [], patterns = []) => {
  if (!Array.isArray(headers) || !headers.length) return -1;
  return headers.findIndex((header) => {
    const key = normalizeKey(header);
    return patterns.some((pattern) => pattern.test(key));
  });
};

export const tableToObjects = (table) => {
  if (!table?.rows?.length) return [];

  const headers =
    Array.isArray(table.headers) && table.headers.length
      ? table.headers
      : table.rows[0].map((_, idx) => `col_${idx + 1}`);

  return table.rows.map((row) => {
    const obj = {};
    headers.forEach((header, idx) => {
      obj[header] = row[idx];
    });
    return obj;
  });
};

const metricValueRows = (table) => {
  if (!table?.rows?.length) return [];

  return table.rows
    .map((row) => {
      if (!Array.isArray(row) || row.length < 2) return null;
      const label = safeText(row[0]);
      const valueCell = row[row.length - 1];
      const value = parseNumber(valueCell);
      if (!label || value === null) return null;

      return {
        label,
        value,
        raw: valueCell,
        isPercent: isPercentLike(valueCell),
      };
    })
    .filter(Boolean);
};

const pickPalette = (size) =>
  Array.from({ length: size }, (_, idx) => PALETTE[idx % PALETTE.length]);

const baseOptions = ({
  indexAxis = "x",
  stacked = false,
  currency = false,
  currencyCode = null,
  percent = false,
  maintainAspectRatio = false,
} = {}) => ({
  ...(() => {
    const theme = getChartTheme();
    return {
  responsive: true,
  maintainAspectRatio,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "top",
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          color: theme.text,
        },
      },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const raw =
            ctx.raw?.y ??
            ctx.raw?.x ??
            ctx.parsed?.y ??
            ctx.parsed?.x ??
            ctx.parsed;
          if (typeof raw !== "number") return `${ctx.dataset.label}: ${raw}`;
          if (percent) {
            return `${ctx.dataset.label}: ${numberFormatter.format(raw)}%`;
          }
          if (currency) {
            return `${ctx.dataset.label}: ${formatCurrencyValue(raw, currencyCode)}`;
          }
          return `${ctx.dataset.label}: ${numberFormatter.format(raw)}`;
        },
      },
    },
  },
  scales: {
    x: {
      stacked,
      ticks: {
        color: theme.tick,
      },
      grid: {
        color: theme.grid,
      },
    },
    y: {
      stacked,
      beginAtZero: true,
      ticks: {
        color: theme.tick,
        callback: (value) => {
          if (percent) return `${value}%`;
          if (currency) return formatCompactCurrency(value, currencyCode);
          return compactFormatter.format(value);
        },
      },
      grid: {
        color: theme.grid,
      },
    },
  },
  indexAxis,
    };
  })(),
});

const buildSimpleBarChart = ({
  rows,
  label = "Value",
  currency = false,
  currencyCode = null,
  percent = false,
  horizontal = false,
}) => {
  if (!rows?.length) return null;

  return {
    type: "bar",
    data: {
      labels: rows.map((item) => item.label),
      datasets: [
        {
          label,
          data: rows.map((item) => item.value),
          backgroundColor: pickPalette(rows.length),
          borderRadius: 10,
        },
      ],
    },
    options: baseOptions({
      indexAxis: horizontal ? "y" : "x",
      currency,
      currencyCode,
      percent,
    }),
  };
};

const buildWideGroupedBarChart = ({
  table,
  labelPatterns = [/channel/, /source/, /name/, /segment/, /cohort/, /sku/, /metric/],
  includePatterns = [],
  currencyPatterns = [
    /revenue/,
    /spend/,
    /cac/,
    /ltv/,
    /aov/,
    /contribution/,
    /profit/,
    /margin per order/,
  ],
  percentPatterns = [/%/, /rate/, /share/, /ratio/, /score/],
  horizontal = false,
}) => {
  if (!table?.rows?.length || !table?.headers?.length) return null;

  const foundIndex = findHeaderIndex(table.headers, labelPatterns);
  const labelIndex = foundIndex >= 0 ? foundIndex : 0;

  const datasets = table.headers
    .map((header, idx) => ({ header, idx }))
    .filter(({ idx }) => idx !== labelIndex)
    .filter(({ header }) => {
      if (!includePatterns.length) return true;
      const key = normalizeKey(header);
      return includePatterns.some((pattern) => pattern.test(key));
    })
    .map(({ header, idx }, datasetIndex) => {
      const values = table.rows.map((row) => parseNumber(row[idx]));
      if (!values.some((value) => value !== null)) return null;

      return {
        label: safeText(header),
        data: values.map((value) => value ?? 0),
        backgroundColor: PALETTE[datasetIndex % PALETTE.length],
        borderRadius: 8,
      };
    })
    .filter(Boolean);

  if (!datasets.length) return null;

  const isCurrencyDataset = datasets.some((dataset) =>
    currencyPatterns.some((pattern) => pattern.test(normalizeKey(dataset.label)))
  );
  const currencyCode = isCurrencyDataset ? inferCurrency(table) : null;

  const isPercentDataset =
    !isCurrencyDataset &&
    datasets.every((dataset) =>
      percentPatterns.some((pattern) => pattern.test(normalizeKey(dataset.label)))
    );

  return {
    type: "bar",
    data: {
      labels: table.rows.map((row) => safeText(row[labelIndex])),
      datasets,
    },
    options: baseOptions({
      indexAxis: horizontal ? "y" : "x",
      currency: isCurrencyDataset,
      currencyCode,
      percent: isPercentDataset,
    }),
  };
};

const buildMixedMetricChart = (rows, currencyCode = null) => {
  if (!rows?.length) return null;
  const theme = getChartTheme();

  return {
    type: "bar",
    data: {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          type: "bar",
          label: "Absolute Value",
          data: rows.map((row) => (row.isPercent ? 0 : row.value)),
          backgroundColor: PALETTE[0],
          borderRadius: 10,
          yAxisID: "y",
        },
        {
          type: "line",
          label: "Percent / Ratio Signal",
          data: rows.map((row) => (row.isPercent ? row.value : null)),
          borderColor: PALETTE[5],
          backgroundColor: PALETTE[5],
          tension: 0.35,
          pointRadius: 4,
          yAxisID: "y1",
        },
      ],
    },
    options: {
      ...baseOptions({ currency: true, currencyCode }),
      scales: {
        x: {
          ticks: { color: theme.tick },
          grid: { color: theme.grid },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: theme.tick,
            callback: (value) => formatCompactCurrency(value, currencyCode),
          },
          grid: { color: theme.grid },
        },
        y1: {
          beginAtZero: true,
          position: "right",
          ticks: {
            color: theme.tick,
            callback: (value) => `${value}%`,
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    },
  };
};

const buildDoughnutFromRows = (rows) => {
  if (!rows?.length) return null;

  return {
    type: "doughnut",
    data: {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: "Share",
          data: rows.map((row) => row.value),
          backgroundColor: pickPalette(rows.length),
          borderWidth: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
        },
        tooltip: {
          callbacks: {
            label: (ctx) =>
              `${ctx.label}: ${numberFormatter.format(ctx.parsed)}%`,
          },
        },
      },
    },
  };
};

const buildRadarFromRows = (rows) => {
  if (!rows?.length) return null;
  const theme = getChartTheme();

  return {
    type: "radar",
    data: {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label: "Score",
          data: rows.map((row) => row.value),
          backgroundColor: "rgba(37, 99, 235, 0.18)",
          borderColor: PALETTE[0],
          pointBackgroundColor: PALETTE[0],
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
      },
      scales: {
        r: {
          beginAtZero: true,
          grid: {
            color: theme.grid,
          },
          angleLines: {
            color: theme.grid,
          },
          pointLabels: {
            color: theme.tick,
            font: {
              size: 11,
            },
          },
          ticks: {
            color: theme.tick,
            backdropColor: "transparent",
          },
        },
      },
    },
  };
};

const buildLineFromRows = (
  rows,
  label = "Value",
  currency = false,
  percent = false,
  currencyCode = null
) => {
  if (!rows?.length) return null;

  return {
    type: "line",
    data: {
      labels: rows.map((row) => row.label),
      datasets: [
        {
          label,
          data: rows.map((row) => row.value),
          borderColor: PALETTE[0],
          backgroundColor: "rgba(37, 99, 235, 0.16)",
          fill: true,
          tension: 0.35,
          pointRadius: 4,
        },
      ],
    },
    options: baseOptions({ currency, currencyCode, percent }),
  };
};

const buildBubbleFromSkuBlocks = (sections = []) => {
  if (!sections?.length) return null;
  const theme = getChartTheme();

  const points = sections
    .map((section, idx) => {
      const rows = metricValueRows(section.table);
      if (!rows.length) return null;

      const revenue =
        rows.find((row) => /revenue/.test(normalizeKey(row.label)))?.value ??
        rows.find((row) => /sales/.test(normalizeKey(row.label)))?.value ??
        idx + 1;

      const contribution =
        rows.find((row) =>
          /contribution after marketing/.test(normalizeKey(row.label))
        )?.value ??
        rows.find((row) => /sku cm2/.test(normalizeKey(row.label)))?.value ??
        rows.find((row) => /profit/.test(normalizeKey(row.label)))?.value ??
        0;

      const size =
        rows.find((row) => /units/.test(normalizeKey(row.label)))?.value ??
        rows.find((row) => /orders/.test(normalizeKey(row.label)))?.value ??
        10;

      return {
        label: section.title,
        x: revenue,
        y: contribution,
        r: Math.max(6, Math.min(24, size / 2)),
      };
    })
    .filter(Boolean);

  if (!points.length) return null;

  return {
    type: "bubble",
    data: {
      datasets: [
        {
          label: "SKU Mix",
          data: points,
          backgroundColor: "rgba(37, 99, 235, 0.45)",
          borderColor: PALETTE[0],
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const point = ctx.raw;
              return `${point.label}: Revenue ${compactFormatter.format(point.x)}, Contribution ${compactFormatter.format(point.y)}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (value) => compactFormatter.format(value),
            color: theme.tick,
          },
          grid: {
            color: theme.grid,
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value) => compactFormatter.format(value),
            color: theme.tick,
          },
          grid: {
            color: theme.grid,
          },
        },
      },
    },
  };
};

export const buildCustomerAcquisitionChart = (table) =>
  buildMixedMetricChart(metricValueRows(table), inferCurrency(table));

export const buildChannelRealOrdersChart = (table) =>
  buildWideGroupedBarChart({
    table,
    labelPatterns: [/channel/, /source/, /platform/],
    includePatterns: [/orders/, /revenue/, /spend/, /cac/, /aov/],
    horizontal: true,
  });

export const buildRepeatCustomersChart = (table) => {
  const rows = metricValueRows(table);
  return buildSimpleBarChart({
    rows,
    label: "Retention Metric",
    percent: rows.length > 0 && rows.every((row) => row.isPercent),
    currencyCode: inferCurrency(table, rows),
  });
};

export const buildScaleSpendChart = (table) =>
  buildSimpleBarChart({
    rows: metricValueRows(table),
    label: "Contribution Outlook",
    currency: true,
    currencyCode: inferCurrency(table),
  });

export const buildRevenueDiversificationChart = (table) => {
  const rows = metricValueRows(table).filter((row) => row.isPercent);
  return buildDoughnutFromRows(rows);
};

export const buildUnitEconomicsChart = (table) =>
  buildSimpleBarChart({
    rows: metricValueRows(table),
    label: "Unit Economics",
    currency: true,
    currencyCode: inferCurrency(table),
  });

export const buildFixedCostViabilityChart = (table) =>
  buildSimpleBarChart({
    rows: metricValueRows(table),
    label: "Fixed Cost Diagnostic",
    currency: true,
    currencyCode: inferCurrency(table),
  });

export const buildCustomerLifetimeEconomicsChart = (table) => {
  const rows = metricValueRows(table);
  const percentOnly = rows.length > 0 && rows.every((row) => row.isPercent);
  return buildLineFromRows(
    rows,
    "Lifetime Signal",
    !percentOnly,
    percentOnly,
    inferCurrency(table)
  );
};

export const buildGrowthEfficiencyChart = (table) =>
  buildWideGroupedBarChart({
    table,
    labelPatterns: [/channel/, /source/, /platform/],
    includePatterns: [/orders/, /revenue/, /ltv/, /cac/, /contribution/, /cm2/],
  });

export const buildMarginStabilityChart = (table) =>
  buildRadarFromRows(metricValueRows(table));

export const buildOperationalReadinessChart = (table) =>
  buildRadarFromRows(metricValueRows(table));

export const buildProductSkuEconomicsChart = (sections) =>
  buildBubbleFromSkuBlocks(sections);
